import { useEffect, useState } from "react";
import { Bot, ShieldCheck, Eye, EyeOff, Database, Share2, ScrollText, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

export function Row({ icon: Icon, title, items, tone = "text-stone-700" }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest ${tone}`}><Icon className="w-3.5 h-3.5" /> {title}</div>
      <ul className="mt-1.5 space-y-1 text-sm text-stone-700 list-disc pl-5">{items.map((t) => <li key={t}>{t}</li>)}</ul>
    </div>
  );
}

export function ProviderRows({ provider }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-stone-700"><Database className="w-3.5 h-3.5" /> What the AI provider keeps</div>
      <ul className="mt-1.5 space-y-1.5 text-sm text-stone-700">
        {provider.map((p) => (
          <li key={p.lane} className="pl-5 relative" data-testid={`ai-provider-${p.lane}`}><span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-stone-400" />
            <strong className="capitalize">{p.lane === "phi" ? "Coach-linked (HIPAA) lane" : "General lane"}</strong>{p.provider ? ` · ${p.provider} / ${p.model}` : ""}: {p.retention}
            {p.legal_review && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-800">· provider terms, verified at launch</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AiLog() {
  const [open, setOpen] = useState(false);
  const [calls, setCalls] = useState(null);
  const toggle = () => { setOpen((o) => !o); if (!calls) api.get("/me/ai-log").then((r) => setCalls(r.data.calls)).catch(() => setCalls([])); };
  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-stone-800" data-testid="ai-log-toggle">
        <span className="inline-flex items-center gap-2"><ScrollText className="w-4 h-4 text-amber-800" /> My AI-use log</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid="ai-log">
          {calls === null ? <div className="text-xs text-stone-500">Loading…</div> : calls.length === 0 ? <div className="text-xs text-stone-500">No AI requests have been made about you yet.</div> : (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {calls.map((c, i) => (
                <li key={i} className="text-xs text-stone-700 flex flex-wrap gap-x-2" data-testid="ai-log-row">
                  <span className="font-mono text-stone-500">{new Date(c.at).toLocaleString()}</span>
                  <span className="font-semibold capitalize">{(c.purpose || "").replace("_", " ")}</span>
                  <span className="text-stone-500">{c.lane === "phi" ? "HIPAA lane" : "general lane"} · {c.model}</span>
                  <span className="text-stone-500">signals: {(c.signals || []).join(", ") || "none"}</span>
                  {c.ok === false && <span className="text-amber-800">fell back</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export const PREF_COPY = [
  ["self", "AI for me", "Personalised rewards, habit-rubric suggestions and the written summary in “Patterns we're noticing”. Off: classic rewards + your favourites + ideas from your coach; rule-based rubrics; plain findings."],
  ["clinician", "AI for my coach about me", "Your coach's AI-assisted view of what you share (de-identified first). Off: they see “AI is off for this member at their request” and get the non-AI version."],
];

export function PrefSwitches({ prefs, onChange }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {PREF_COPY.map(([key, title, desc]) => (
        <label key={key} className={`flex items-start gap-3 rounded-2xl border p-4 cursor-pointer ${prefs[key] ? "bg-emerald-50/60 border-emerald-200" : "bg-stone-50 border-stone-200"}`} data-testid={`ai-pref-${key}`}>
          <Switch checked={!!prefs[key]} onCheckedChange={(v) => onChange(key, v)} data-testid={`ai-pref-${key}-switch`} className="mt-0.5" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-stone-900">{title} <span className={`ml-1 text-[10px] font-bold uppercase tracking-wider ${prefs[key] ? "text-emerald-700" : "text-stone-500"}`} data-testid={`ai-pref-${key}-state`}>{prefs[key] ? "on" : "off"}</span></span>
            <span className="block text-xs text-stone-600 mt-0.5">{desc}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

export function Disclosure({ disclosure, expanded, onToggle }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 space-y-4" data-testid="ai-disclosure">
      <div className="flex items-center gap-2 text-sm font-bold text-stone-900"><ShieldCheck className="w-4 h-4 text-amber-800" /> What the AI sees and keeps</div>
      <Row icon={Eye} title="What's sent (de-identified)" items={disclosure.sent} />
      <Row icon={EyeOff} title="Never sent" items={disclosure.never_sent} tone="text-rose-800" />
      {expanded && (
        <>
          <Row icon={Database} title="What Root & Rose stores" items={disclosure.stored} />
          <ProviderRows provider={disclosure.provider} />
          <Row icon={Share2} title="Sharing and training" items={disclosure.sharing} />
          <p className="text-[11px] text-stone-500">De-identification level: <span className="font-mono">{disclosure.deid}</span>. No AI provider trains on your data.</p>
        </>
      )}
      <button type="button" onClick={onToggle} className="text-xs font-semibold text-amber-900 underline underline-offset-2" data-testid="ai-disclosure-more">{expanded ? "Show less" : "Show what's stored, what the provider keeps, and sharing"}</button>
    </div>
  );
}

function DeleteHistory({ onDeleted }) {
  const [arming, setArming] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/me/ai-history");
      const d = r.data.deleted;
      toast.success(`Deleted ${d.rewards} reward${d.rewards === 1 ? "" : "s"}, ${d.pattern_summaries} summar${d.pattern_summaries === 1 ? "y" : "ies"} and ${d.rubric_suggestions + d.ai_outputs} AI suggestion${d.rubric_suggestions + d.ai_outputs === 1 ? "" : "s"}. Points and habits untouched.`);
      setArming(false);
      onDeleted?.();
    } catch { toast.error("Couldn't delete right now."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4" data-testid="ai-delete-history">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[14rem]">
          <div className="flex items-center gap-2 text-sm font-bold text-stone-900"><Trash2 className="w-4 h-4 text-rose-700" /> Delete my AI history</div>
          <p className="text-xs text-stone-600 mt-1">Permanently removes every AI-written reward (claimed or not), rubric suggestion and pattern summary about you. Your points, habits and check-ins stay. Your AI-use log stays too — it holds no content and is part of your audit trail.</p>
        </div>
        {!arming ? (
          <button onClick={() => setArming(true)} className="text-xs font-semibold px-3 py-2 rounded-full border border-rose-300 text-rose-800 hover:bg-rose-600 hover:text-white transition-colors" data-testid="ai-delete-history-btn">Delete AI history</button>
        ) : (
          <div className="flex items-center gap-2" data-testid="ai-delete-history-confirm">
            <span className="text-xs font-semibold text-rose-800">Are you sure? This can't be undone.</span>
            <button onClick={() => setArming(false)} className="text-xs font-semibold px-3 py-2 rounded-full border border-stone-300 text-stone-700" data-testid="ai-delete-history-cancel">Keep it</button>
            <button onClick={run} disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60" data-testid="ai-delete-history-yes">{busy ? "Deleting…" : "Yes, delete"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AiChoicesCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showMore, setShowMore] = useState(false);
  useEffect(() => { api.get("/me/ai-preferences").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (user?.role !== "client" || !data) return null;
  const { prefs, disclosure } = data;
  const set = async (key, value) => {
    setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: value } }));
    try {
      await api.patch("/me/ai-preferences", { [key]: value });
      toast.success(value ? "AI turned back on." : key === "self" ? "AI is off for you. Rewards and suggestions now come from built-in picks." : "AI is off for your coach's tools about you.");
    } catch { toast.error("Couldn't save that choice."); setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: !value } })); }
  };
  return (
    <section className="warm-card p-6 space-y-5" data-testid="ai-choices-card">
      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-800"><Bot className="w-4 h-4" /> AI choices</div>
        <h3 className="font-display text-xl font-bold text-stone-900 mt-1">Decide how AI is used with your account</h3>
        <p className="text-sm text-stone-600 mt-1">Both are on by default. Turn either off any time — the app keeps working with built-in, non-AI versions. Every change is recorded in your audit trail.</p>
      </div>
      <PrefSwitches prefs={prefs} onChange={set} />
      <Disclosure disclosure={disclosure} expanded={showMore} onToggle={() => setShowMore((v) => !v)} />
      <DeleteHistory />
      <AiLog />
    </section>
  );
}
